import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { showHubTeamToast } from "../utils/hub_team_toast"

export default class extends Controller {
  static targets = [
    "modal",
    "errorAlert",
    "errorMessage",
    "emptyState",
    "activeState",
    "domainRow",
    "domainInput",
    "emptyDomains",
    "activeDomains",
    "urlInput",
    "copyButton",
    "generateButton",
    "saveButton",
    "revokeButton"
  ]
  static values = { url: String }

  open(event) {
    event.preventDefault()
    this.clearError()
    if (this.hasModalTarget) Modal.getOrCreateInstance(this.modalTarget).show()
  }

  addDomain(event) {
    event.preventDefault()
    const list = event.currentTarget.closest("form")?.querySelector("[data-enrollment-link-modal-target='emptyDomains'], [data-enrollment-link-modal-target='activeDomains']")
    const source = list?.querySelector("[data-enrollment-link-modal-target='domainRow']")
    if (!list || !source) return

    const clone = source.cloneNode(true)
    const input = clone.querySelector("input")
    if (input) input.value = ""
    const label = clone.querySelector("label")
    if (label) {
      label.textContent = "Another allowed domain"
      label.classList.add("visually-hidden")
    }
    list.appendChild(clone)
  }

  async generate(event) {
    event.preventDefault()
    await this.submit("POST", this.domainsFrom(event.currentTarget), this.hasGenerateButtonTarget ? this.generateButtonTarget : null)
  }

  async saveDomains(event) {
    event.preventDefault()
    await this.submit("PATCH", this.domainsFrom(event.currentTarget), this.hasSaveButtonTarget ? this.saveButtonTarget : null)
  }

  async revoke(event) {
    event.preventDefault()
    if (!window.confirm("People with this link will no longer be able to enroll. Revoke it?")) return

    await this.submit("DELETE", [], this.hasRevokeButtonTarget ? this.revokeButtonTarget : null)
  }

  domainsFrom(form) {
    return Array.from(form.querySelectorAll("input[name='email_domains[]']"))
      .map((input) => input.value.trim())
      .filter((value) => value.length > 0)
  }

  async submit(method, domains, button) {
    if (!this.hasUrlValue) return

    this.setSaving(button, true)
    this.clearError()

    const body = new URLSearchParams()
    domains.forEach((domain) => body.append("email_domains[]", domain))

    try {
      const response = await fetch(this.urlValue, {
        method,
        body: method === "DELETE" ? null : body,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          Accept: "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.applyPayload(data)
        this.showToast(data.message)
        if (!this.hasModalTarget) window.location.reload()
        return
      }

      const message = data?.errors?.join(", ") || `HTTP ${response.status}`
      this.showError(message)
    } catch (error) {
      console.error("Enrollment link request failed:", error)
      this.showError("An error occurred. Please try again.")
    } finally {
      this.setSaving(button, false)
    }
  }

  applyPayload(data) {
    if (data.active && data.url) {
      this.showActive(data)
      return
    }

    this.showEmpty()
  }

  showActive(data) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true
    if (this.hasActiveStateTarget) this.activeStateTarget.hidden = false
    if (this.hasUrlInputTarget) this.urlInputTarget.value = data.url

    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.disabled = false
      this.copyButtonTarget.dataset.clipboardContentValue = data.url
    }

    this.replaceDomainInputs(this.hasActiveDomainsTarget ? this.activeDomainsTarget : null, data.domains || [])
  }

  showEmpty() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = false
    if (this.hasActiveStateTarget) this.activeStateTarget.hidden = true
    if (this.hasUrlInputTarget) this.urlInputTarget.value = ""
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.disabled = true
      this.copyButtonTarget.dataset.clipboardContentValue = ""
    }
  }

  replaceDomainInputs(list, domains) {
    if (!list) return

    const rows = list.querySelectorAll("[data-enrollment-link-modal-target='domainRow']")
    const template = rows[0]
    if (!template) return

    list.replaceChildren()
    const values = domains.length > 0 ? domains : [""]
    values.forEach((domain, index) => {
      const row = template.cloneNode(true)
      const input = row.querySelector("input")
      if (input) input.value = domain
      const label = row.querySelector("label")
      if (label) {
        label.textContent = index === 0 ? "Allowed domains" : "Another allowed domain"
        label.classList.toggle("visually-hidden", index > 0)
      }
      list.appendChild(row)
    })
  }

  setSaving(button, saving) {
    if (!button) return

    button.disabled = saving
    button.dataset.saving = saving ? "true" : "false"
  }

  showError(message) {
    if (this.hasErrorAlertTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorAlertTarget.classList.remove("d-none")
      return
    }

    this.showToast(message, "error")
  }

  clearError() {
    if (!this.hasErrorAlertTarget || !this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = ""
    this.errorAlertTarget.classList.add("d-none")
  }

  showToast(message, variant = "info") {
    if (typeof showHubTeamToast === "function") showHubTeamToast(message, { variant })
  }
}
